import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const CountryContext = createContext();
export const useCountries = () => useContext(CountryContext);

export const CountryProvider = ({ children }) => {
  const { token, adminId, isReady, logout } = useAuth();

  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  const COUNTRIES_ENDPOINT = "/countries";
  const baseURL = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;

  /* =========================
     Mount Safety
  ========================= */
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetCountries = (updater) => {
    if (!mountedRef.current) return;
    setCountries(updater);
  };

  /* =========================
     FETCH
  ========================= */
  const fetchCountries = useCallback(async () => {
    setLoading(true);
    try {
      if (!token) throw new Error("Missing auth token");

      const res = await fetch(`${baseURL}${COUNTRIES_ENDPOINT}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await res.json().catch(() => ({}));
      if (!res.ok)
        throw new Error(payload?.message || `Fetch failed (${res.status})`);

      safeSetCountries(payload?.data || []);
      return payload;
    } catch (err) {
      console.error("[Countries] fetchCountries error:", err);
      if (err.message?.includes("Token")) logout();
      safeSetCountries([]);
      throw err;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token, baseURL]);

  /* =========================
     ADD
  ========================= */
  const addCountry = useCallback(
    async (data) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${COUNTRIES_ENDPOINT}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...data,
            create_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok)
          throw new Error(payload?.message || `Add failed (${res.status})`);

        await fetchCountries();
        return payload;
      } catch (err) {
        console.error("[Countries] addCountry error:", err);
        throw err;
      }
    },
    [token, baseURL, fetchCountries, adminId]
  );

  /* =========================
     UPDATE
  ========================= */
  const updateCountry = useCallback(
    async (id, data) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${COUNTRIES_ENDPOINT}/${id}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            ...data,
            update_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok)
          throw new Error(payload?.message || `Update failed (${res.status})`);

        await fetchCountries();
        return payload;
      } catch (err) {
        console.error("[Countries] updateCountry error:", err);
        throw err;
      }
    },
    [token, baseURL, fetchCountries, adminId]
  );

  /* =========================
     DELETE (SOFT)
  ========================= */
  const deleteCountry = useCallback(
    async (id) => {
      try {
        if (!token) throw new Error("Missing auth token");

        const res = await fetch(`${baseURL}${COUNTRIES_ENDPOINT}/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            delete_audit_id: adminId,
          }),
        });

        const payload = await res.json().catch(() => ({}));
        if (!res.ok)
          throw new Error(payload?.message || `Delete failed (${res.status})`);

        safeSetCountries((prev) =>
          prev.filter((c) => String(c.id) !== String(id))
        );

        return payload;
      } catch (err) {
        console.error("[Countries] deleteCountry error:", err);
        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* =========================
     TOGGLE STATUS
  ========================= */
  const toggleStatus = useCallback(
    async (id, currentStatus) => {
      const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

      try {
        if (!token) throw new Error("Missing auth token");

        // Optimistic UI
        safeSetCountries((prev) =>
          prev.map((c) =>
            String(c.id) === String(id)
              ? { ...c, status: newStatus }
              : c
          )
        );

        const res = await fetch(
          `${baseURL}${COUNTRIES_ENDPOINT}/${id}/status`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              status: newStatus,
              update_audit_id: adminId,
            }),
          }
        );

        const payload = await res.json().catch(() => ({}));
        if (!res.ok)
          throw new Error(payload?.message || `Toggle failed (${res.status})`);

        return payload;
      } catch (err) {
        console.error("[Countries] toggleStatus error:", err);

        // Rollback UI
        safeSetCountries((prev) =>
          prev.map((c) =>
            String(c.id) === String(id)
              ? { ...c, status: currentStatus }
              : c
          )
        );

        throw err;
      }
    },
    [token, baseURL, adminId]
  );

  /* =========================
     INITIAL FETCH
  ========================= */
  useEffect(() => {
    if (!isReady || !token) return;
    fetchCountries().catch(() => {});
  }, [isReady, token]);

  return (
    <CountryContext.Provider
      value={{
        countries,
        loading,
        fetchCountries,
        addCountry,
        updateCountry,
        deleteCountry,
        toggleStatus,
      }}
    >
      {children}
    </CountryContext.Provider>
  );
};
